import { useCallback, useEffect, useMemo, useState } from 'react';
import { css } from '@emotion/react';
import {
  autoUpdate,
  flip,
  FloatingFocusManager,
  FloatingNode,
  FloatingPortal,
  offset,
  useClick,
  useDismiss,
  useFloating,
  useFloatingNodeId,
  useInteractions,
  useRole,
} from '@floating-ui/react';
import { kebabCase } from 'lodash-es';
import { CallIntelTypes } from '@frontend/api-call-intel';
import { useTranslation } from '@frontend/i18n';
import { Icon } from '@frontend/icons';
import type { WeaveTheme as OriginalTheme } from '@frontend/theme-original';
import { ComponentProps } from '@frontend/types';
import { theme, WeaveTheme } from '@frontend/theme';
import {
  ChecklistField,
  FieldChangeEvent,
  FieldLayout,
  FieldLayoutWithAction,
  RadioField,
  useFormField,
  useThemeValues,
} from '@frontend/design-system';
import { useOptionsProvider } from '../../hooks';
import { useIsMobile } from '../../hooks/use-is-mobile';
import { resolveTrackingPlaceholders } from '../../utils';
import { SchedulingChip } from '../chip/scheduling-chip';
import { BadgeDropdownInput } from '../input/badge-dropdown-input';
import EditMenu from '../menu/edit-menu';

export type BadgeOption = {
  chip: React.ReactNode;
  label?: string;
};

type WidthConfig = {
  menuWidth?: string;
  fieldWidth?: string;
};

type Props = {
  emptyValue?: string;
  isLoading?: boolean;
  label?: string;
  name: string;
  onChange?: (e?: FieldChangeEvent) => void;
  onSuggestion?: (name: string) => void;
  options: Record<string, BadgeOption>;
  required?: boolean;
  showSaveChanges?: boolean;
  showSuggestion?: boolean;
  trackingIds?: {
    fieldId?: string;
    optionId?: string;
    saveBtnId?: string;
    suggestionBtnId?: string;
  };
  useFormLevelSave?: boolean;
  value?: string;
  widthConfig?: WidthConfig;
  unsavedEditChangesConfig: CallIntelTypes.UnsavedEditChangesConfig;
  setUnsavedEditChangesConfig: (config: CallIntelTypes.UnsavedEditChangesConfig) => void;
};

export const MAX_FLOATING_HEIGHT = 400;
export const MAX_FLOATING_WIDTH = 300;

const SchedulingOutcomeDropdownMenu = ({
  emptyValue = '-',
  isLoading = false,
  label = '',
  name,
  onChange,
  onSuggestion,
  options,
  required = false,
  showSaveChanges = true,
  showSuggestion = false,
  trackingIds,
  useFormLevelSave = true,
  value = '',
  widthConfig = {
    fieldWidth: '100%',
    menuWidth: '166px',
  },
  unsavedEditChangesConfig,
  setUnsavedEditChangesConfig,
}: Props) => {
  const { t } = useTranslation('analytics');
  const isMobile = useIsMobile();
  const { getOptions } = useOptionsProvider();

  const [isOpen, setIsOpen] = useState(false);

  const theme = useThemeValues();

  const fieldProps = useFormField(
    {
      type: 'radio',
      value,
      required,
    },
    [value]
  );

  const childFieldProps = useFormField({
    type: 'checklist',
    value:
      value === CallIntelTypes.SchedulingOutcomeV2Enum.SCHEDULING_OUTCOME_SCHEDULED_POST_CALL
        ? [CallIntelTypes.SchedulingOutcomeV2Enum.SCHEDULING_OUTCOME_SCHEDULED_POST_CALL]
        : [],
  });

  const isRecoveredOpportunity =
    CallIntelTypes.SchedulingOutcomeV2Enum.SCHEDULING_OUTCOME_SCHEDULED_POST_CALL === fieldProps.value;

  const internalValue = isRecoveredOpportunity
    ? CallIntelTypes.SchedulingOutcomeV2Enum.SCHEDULING_OUTCOME_SCHEDULED_POST_CALL
    : fieldProps.value;

  const radioOptions = useMemo(
    () =>
      getOptions('schedulingOutcomeV2', 'array', {
        excludeOptions: [CallIntelTypes.SchedulingOutcomeV2Enum.SCHEDULING_OUTCOME_SCHEDULED_POST_CALL],
      }),
    [getOptions]
  );

  const nodeId = useFloatingNodeId();

  const isChanged = useFormLevelSave ? true : (!!fieldProps.error && required) || fieldProps.value !== value;

  const { refs, context, floatingStyles } = useFloating<HTMLInputElement>({
    nodeId,
    whileElementsMounted: autoUpdate,
    open: isOpen,
    onOpenChange: setIsOpen,
    middleware: [offset(4), flip()],
    placement: !isMobile ? 'bottom-end' : 'bottom-start',
  });

  const { getReferenceProps } = useInteractions([
    useClick(context),
    useRole(context, { role: 'listbox' }),
    useDismiss(context, {
      outsidePress: (event) => {
        const isParent = (event?.target as HTMLElement)?.closest('.edit-menu');
        const isUnsavedChangesModal = (event?.target as HTMLElement)?.closest('.unsaved-changes-modal');

        if (isParent) {
          return false;
        }
        if (!useFormLevelSave) {
          if (isUnsavedChangesModal) {
            return false;
          }
          if (isChanged && !unsavedEditChangesConfig.dontAskAgain) {
            setUnsavedEditChangesConfig({ actionType: 'SHOW_MODAL', entity: name, hasUnsavedChanges: true });
            return false;
          }
          fieldProps.onChange({ name, value });
        }
        fieldProps.onBlur();
        return !(event?.target as HTMLElement)?.closest('svg');
      },
    }),
  ]);

  const refProps = getReferenceProps({
    onClick: () => {
      fieldProps.onBlur();
    },
  });

  const handleChange = useCallback(
    (e?: FieldChangeEvent) => {
      //@ts-ignore - until FieldChangeEvent is fixed
      const value = e?.target?.value ?? e.value;
      fieldProps.onChange({ name, value });
      if (
        childFieldProps.value.length &&
        value !== CallIntelTypes.SchedulingOutcomeV2Enum.SCHEDULING_OUTCOME_UNSCHEDULED
      ) {
        childFieldProps.onChange({ name: 'subMenu', value: [] });
      }
      useFormLevelSave && !!onChange && onChange(e);
    },
    [childFieldProps, fieldProps, name, onChange, useFormLevelSave]
  );

  const handleSecondaryChange = useCallback(
    (e?: FieldChangeEvent) => {
      //@ts-ignore - until FieldChangeEvent is fixed
      const toggledValue = e.value;
      const currentValues = childFieldProps.value;

      let newValues = [];

      if (currentValues.includes(toggledValue)) {
        newValues = currentValues.filter((item) => item !== toggledValue);
      } else {
        newValues = [...currentValues, toggledValue];
      }

      const isRecoveredOpportunity = newValues.includes(
        CallIntelTypes.SchedulingOutcomeV2Enum.SCHEDULING_OUTCOME_SCHEDULED_POST_CALL
      );

      if (isRecoveredOpportunity) {
        fieldProps.onChange({
          name,
          value: CallIntelTypes.SchedulingOutcomeV2Enum.SCHEDULING_OUTCOME_SCHEDULED_POST_CALL,
        });
        useFormLevelSave &&
          !!onChange &&
          onChange({ name, value: CallIntelTypes.SchedulingOutcomeV2Enum.SCHEDULING_OUTCOME_SCHEDULED_POST_CALL });
      } else {
        fieldProps.onChange({
          name,
          value: CallIntelTypes.SchedulingOutcomeV2Enum.SCHEDULING_OUTCOME_UNSCHEDULED,
        });
        useFormLevelSave &&
          !!onChange &&
          onChange({ name, value: CallIntelTypes.SchedulingOutcomeV2Enum.SCHEDULING_OUTCOME_UNSCHEDULED });
      }

      childFieldProps.onChange({ name: 'subMenu', value: newValues });
    },
    [childFieldProps, fieldProps, name, onChange, useFormLevelSave]
  );

  const handleSave = useCallback(() => {
    if (!useFormLevelSave) {
      !!onChange && onChange({ name, value: fieldProps.value });
    }
  }, [fieldProps.value, name, onChange, useFormLevelSave]);

  useEffect(() => {
    if (!useFormLevelSave && !isLoading && isOpen) {
      setIsOpen(false);
    }
  }, [isLoading, useFormLevelSave]);

  useEffect(() => {
    const { actionType, entity, hasUnsavedChanges } = unsavedEditChangesConfig;
    if (!useFormLevelSave && hasUnsavedChanges && entity === name) {
      if (actionType === 'SAVE') {
        handleSave();
      } else if (actionType === 'CLOSE') {
        setUnsavedEditChangesConfig({ hasUnsavedChanges: true, actionType: 'NO_ACTION' });
      } else if (actionType === 'DISCARD') {
        setUnsavedEditChangesConfig({ hasUnsavedChanges: false, actionType: 'NO_ACTION' });
        fieldProps.onChange({ name, value });
        setIsOpen(false);
      }
    }
  }, [useFormLevelSave, unsavedEditChangesConfig]);

  return (
    <FieldLayout
      label=''
      /**
       * The types don't know that the fieldComponentProps are being passed to component here
       * We'll cast it for now until the types are fixed
       */
      field={BadgeDropdownInput as unknown as ComponentProps<typeof FieldLayoutWithAction>['field']}
      fieldComponentProps={{
        emptyValue,
        trackingId: trackingIds?.fieldId,
        options,
      }}
      css={[
        styles.fieldLayout,
        css`
          width: ${widthConfig.fieldWidth};
        `,
      ]}
      className='checklist-tag-menu-field'
      containerCss={css({
        ':focus': {
          outline: 'none',
        },
        ':focus .checklist-tag-menu-field': {
          outline: `1px solid ${(theme as WeaveTheme).colors.primary50 ?? (theme as OriginalTheme).colors.weaveBlue}`,
          borderColor: (theme as WeaveTheme).colors.primary50 ?? (theme as OriginalTheme).colors.weaveBlue,
        },
      })}
      endAdornment={
        <Icon
          css={[
            css`
              cursor: pointer;
              transition: transform 250ms ease-out;
            `,

            isOpen &&
              css`
                transform: rotate(180deg);
              `,
          ]}
          color='subdued'
          data-trackingid={trackingIds?.fieldId}
          name='alt-caret-down-tiny'
          onClick={() => setIsOpen((prev) => !prev)}
          size={8}
        />
      }
      ref={refs.setReference}
      {...refProps}
      {...fieldProps}
      name={name}
      hasPadding={false}
      onChange={() => {}}
      hideErrorText
    >
      <FloatingNode id={nodeId}>
        {isOpen && (
          <FloatingPortal>
            <FloatingFocusManager context={context}>
              <EditMenu
                errorMsg={
                  !!fieldProps.error && required
                    ? t(!label ? fieldProps.error : t(`Please select at least one ${label}.`))
                    : ''
                }
                isLoading={isLoading}
                isPrimaryDisabled={!isChanged}
                ref={refs.setFloating}
                onSave={handleSave}
                onSuggest={() => {
                  !!onSuggestion && onSuggestion(name);
                }}
                showSaveChanges={showSaveChanges}
                showSuggestion={showSuggestion}
                style={{
                  ...floatingStyles,
                  boxShadow: theme.shadows.heavy,
                  borderRadius: theme.borderRadius.small,
                  backgroundColor: theme.colors.white,
                  zIndex: theme.zIndex.modals,
                  overflow: 'hidden',
                  maxHeight: MAX_FLOATING_HEIGHT,
                  maxWidth: MAX_FLOATING_WIDTH,
                  width: widthConfig.menuWidth,
                }}
                trackingIds={{ saveBtnId: trackingIds?.saveBtnId, suggestionBtnId: trackingIds?.suggestionBtnId }}
              >
                <RadioField
                  disabled={false}
                  label=''
                  {...fieldProps}
                  name={name}
                  error=''
                  css={styles.fieldWrapper}
                  onChange={handleChange}
                  value={internalValue}
                >
                  {radioOptions.map(({ id }) => {
                    const isUnscheduledOption =
                      id === CallIntelTypes.SchedulingOutcomeV2Enum.SCHEDULING_OUTCOME_UNSCHEDULED;
                    return (
                      <div key={id}>
                        <RadioField.Radio
                          css={styles.optionList}
                          value={
                            isRecoveredOpportunity && isUnscheduledOption
                              ? CallIntelTypes.SchedulingOutcomeV2Enum.SCHEDULING_OUTCOME_SCHEDULED_POST_CALL
                              : id
                          }
                          trackingId={resolveTrackingPlaceholders(trackingIds?.optionId ?? 'badge-dropdown', {
                            menuOption: kebabCase(`option-type-${id?.toLowerCase()}`),
                          })}
                        >
                          <div css={styles.optionWrapper}>
                            <SchedulingChip
                              schedulingOutcome={id as CallIntelTypes.SchedulingOutcomeV2Enum}
                              showTooltip={false}
                              disabled={isUnscheduledOption && isRecoveredOpportunity}
                            />
                          </div>
                        </RadioField.Radio>
                        {isUnscheduledOption && (
                          <div css={styles.nestedChipWrapper}>
                            <span css={styles.pipeBelow} />
                            <ChecklistField
                              css={styles.nestedChecklist}
                              label=''
                              name={'subMenu'}
                              {...childFieldProps}
                              onChange={handleSecondaryChange}
                            >
                              <ChecklistField.Option
                                name={CallIntelTypes.SchedulingOutcomeV2Enum.SCHEDULING_OUTCOME_SCHEDULED_POST_CALL}
                                trackingId={resolveTrackingPlaceholders(trackingIds?.optionId ?? 'badge-dropdown', {
                                  menuOption: kebabCase(
                                    `option-type-${CallIntelTypes.SchedulingOutcomeV2Enum.SCHEDULING_OUTCOME_SCHEDULED_POST_CALL?.toLowerCase()}`
                                  ),
                                })}
                              >
                                <SchedulingChip
                                  schedulingOutcome={
                                    CallIntelTypes.SchedulingOutcomeV2Enum.SCHEDULING_OUTCOME_SCHEDULED_POST_CALL
                                  }
                                  showTooltip={false}
                                />
                              </ChecklistField.Option>
                            </ChecklistField>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </RadioField>
              </EditMenu>
            </FloatingFocusManager>
          </FloatingPortal>
        )}
      </FloatingNode>
    </FieldLayout>
  );
};

export default SchedulingOutcomeDropdownMenu;

const styles = {
  fieldWrapper: css`
    padding: ${theme.spacing(2, 0, 0, 0)};

    > div {
      padding: ${theme.spacing(1, 2)};
      border-bottom: 1px solid ${theme.colors.neutral20};
      > div {
        margin: 0;
      }
    }
  `,
  fieldLayout: css`
    display: grid;
    gap: ${theme.spacing(1)};
    align-items: center;
    grid-template-columns: 1fr auto;
    padding: ${theme.spacing(1)};
    cursor: pointer;
    min-height: ${theme.spacing(5)};
    height: ${theme.spacing(5)};
    max-width: ${theme.spacing(32)};
  `,
  chip: css`
    max-width: none;
  `,
  optionList: css`
    align-items: center;
  `,
  optionWrapper: css`
    display: flex;
    align-items: center;
    gap: ${theme.spacing(1)};
  `,

  nestedChecklist: css`
    > div {
      margin: 0;
    }
  `,

  nestedChipWrapper: css`
    position: relative;
    display: flex;
    align-items: center;
    gap: ${theme.spacing(0.5)};
  `,

  pipeBelow: css`
    position: relative;
    display: inline-block;
    width: ${theme.spacing(2.25)};
    height: ${theme.spacing(3.25)};
    border: 1px solid ${theme.colors.neutral20};
    border-radius: ${theme.borderRadius.small};
    border-top: none;
    border-right: none;
    border-bottom-right-radius: ${theme.spacing(0)};
    border-top-left-radius: ${theme.spacing(0)};
    top: ${theme.spacing(-1)};
    left: ${theme.spacing(1)};
    margin-right: ${theme.spacing(1)};
  `,
};
